import { CSSProperties } from "react";
import { clsx, styx } from "modules/Utils";
import useCampaignBuilder, {
    CURRENT_BUDGET_TABS,
    CURRENT_DATE_SELECTION_TABS,
    CurrentDateSelectionTabType
} from 'Hooks/useCampaignBuilder';
import dayjs from 'dayjs';
import 'styles/react-datepicker.css';
import Colors from "modules/Colors";
import TabSymphony from "elements/TabSymphony";
import InputSymphony, { SelectOptionItem } from "elements/InputSymphony";
import useScreen from "Hooks/useScreen";
import CardSymphony from "elements/CardSymphony";
import Sty from "modules/Sty";
import DatePickerSymphony from "elements/DatePickerSymphony";
import { AdPlatform } from "modules/Const";
import DebugBox from "components/shareable/DebugBox";

/** Options for lifetime budget campaign dropdown */
const LIFETIME_BUDGET_DATE_CAMPAIGN_OPTIONS: SelectOptionItem[] = [
    {
        value: CURRENT_DATE_SELECTION_TABS.DATE_RANGE,
        title: 'Between these dates'
    },
    {
        value: CURRENT_DATE_SELECTION_TABS.NUMBER_OF_DAYS,
        title: 'For this many days'
    }
]

/** Options for daily budget campaign dropdown including maximum budget */
const DAILY_BUDGET_DATE_CAMPAIGN_OPTIONS: SelectOptionItem[] = [
    ...LIFETIME_BUDGET_DATE_CAMPAIGN_OPTIONS,
    {
        value: CURRENT_DATE_SELECTION_TABS.MAXIMUM_BUDGET,
        title: "Until it's spent"
    }
]

/** Minimum daily budget required for ads */
const MINIMUM_DAILY_BUDGET = 10;

interface DatesCampaignProps {
    className?: string;
    style?: CSSProperties;
    title?: string;
    description?: string;
}

const DatesCampaign = ({
    className,
    style,
    title,
    description,
}: DatesCampaignProps) => {
    const {
        mStyle
    } = useScreen();
    const {
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        numberOfDays,
        setNumberOfDays,
        currentDateSelectionTab,
        setCurrentDateSelectionTab,
        currentBudgetTab,
        budgetMaximum,
        setBudgetMaximum,
        budget,
        adPlatform
    } = useCampaignBuilder();

    /** Calculate the maximum allowed end date based on budget to ensure $10/day minimum */
    const getMaxEndDate = () => {
        if (currentBudgetTab !== CURRENT_BUDGET_TABS.LIFETIME || !startDate || !budget || budget === '') {
            return undefined;
        }

        const budgetNumber = Number(budget);
        if (isNaN(budgetNumber) || budgetNumber <= 0) {
            return undefined;
        }

        // Calculate max days based on $10/day minimum
        const maxDays = Math.floor(budgetNumber / MINIMUM_DAILY_BUDGET);
        if (maxDays <= 0) {
            return undefined;
        }

        // Max end date is start date + (maxDays - 1) to account for inclusive counting
        return dayjs(startDate).add(maxDays - 1, 'days').toDate();
    };

    /** Calculate the minimum allowed end date (always at least 1 day after start date) */
    const getMinEndDate = () => {
        if (!startDate) {
            return new Date();
        }

        // Minimum end date is always the day after start date
        return dayjs(startDate).add(1, 'day').toDate();
    };

    const getMinStartDate = () => {
        if (adPlatform === AdPlatform.TIKTOK) {
            return dayjs().add(1, 'day').toDate();
        }
        return new Date();
    }

    const handleStartDateChange = (date: Date | null) => {
        if (!date) {
            return;
        }

        const newStartDate = dayjs(date).format('YYYY-MM-DD');
        // setStartDate(newStartDate);

        // If not in DATE_RANGE mode, don't update start date
        if (currentDateSelectionTab !== CURRENT_DATE_SELECTION_TABS.DATE_RANGE) {
            return
        }

        // If in DATE_RANGE mode and end date is not same as or before the new start date, update it
        if (!dayjs(endDate).isSame(newStartDate) || !dayjs(endDate).isBefore(newStartDate)) {
            setStartDate(newStartDate);
        }
    };

    const handleEndDateChange = (date: Date | null) => {
        if (!date) {
            return;
        }

        setEndDate(dayjs(date).format('YYYY-MM-DD'));
    };

    const handleNumberOfDaysStartDateChange = (date: Date | null) => {
        if (!date) {
            return;
        }

        const newStartDate = dayjs(date).format('YYYY-MM-DD');
        // setStartDate(newStartDate);
        // Ensure numberOfDays is at least 1 when calculating end date
        const effectiveDays = numberOfDays && numberOfDays > 0 ? numberOfDays : 1;
        setStartDate(newStartDate);
        setEndDate(dayjs(date).add(effectiveDays, 'days').format('YYYY-MM-DD'));
    };

    const renderDateDivider = () => {
        return (
            <div className="dateDivider" style={sty.dateDivider}>
                <div style={sty.dateDividerLine}></div>
                <div style={sty.dateDividerText}>to</div>
                <div style={sty.dateDividerLine}></div>
            </div>
        )
    }

    const renderDateRangeContent = () => {
        // Calculate maximum end date based on budget
        const maxEndDate = getMaxEndDate();
        const budgetLimitsDateRange = !!maxEndDate;

        return (
            <>
                <TabSymphony
                    className="dateRangeContent"
                    style={mStyle(sty, 'dateRangeContent')}
                    visible={currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.DATE_RANGE}
                >
                    <DatePickerSymphony
                        label="Start Date"
                        placeholderText="Set start date"
                        minDate={getMinStartDate()}
                        filterDate={(date: Date) => {
                            if (!startDate || !endDate) {
                                return true
                            }

                            const localDate = dayjs(new Date(date)).format('YYYY-MM-DD');
                            const localEndDate = dayjs(endDate).format('YYYY-MM-DD');

                            return dayjs(localDate).isBefore(dayjs(localEndDate));
                        }}
                        selected={startDate ? dayjs(startDate).toDate() : null}
                        onChange={handleStartDateChange}
                        dateFormat="MMMM dd, yyyy"
                        maxWidth={300}
                    />
                    {renderDateDivider()}
                    <DatePickerSymphony
                        label="End Date"
                        placeholderText="Set end date"
                        minDate={getMinEndDate()}
                        maxDate={maxEndDate}
                        selected={endDate ? dayjs(endDate).toDate() : null}
                        onChange={handleEndDateChange}
                        dateFormat="MMMM dd, yyyy"
                        maxWidth={300}
                    />
                </TabSymphony>
                {currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.DATE_RANGE &&
                    budgetLimitsDateRange &&
                    budget &&
                    budget !== '' &&
                    <div style={styx(Sty.text12grey, sty.helperTextContainer)}>
                        Date range limited by budget (${MINIMUM_DAILY_BUDGET}/day minimum)
                    </div>
                }
            </>
        )
    }

    const renderNumberOfDaysContent = () => {
        // Calculate maximum days based on budget
        let maxAllowedDays: number | undefined = undefined;
        if (budget && budget !== '' && !isNaN(Number(budget)) && Number(budget) > 0) {
            maxAllowedDays = Math.floor(Number(budget) / MINIMUM_DAILY_BUDGET);
        }

        return (
            <>
                <TabSymphony
                    className="daysInputContainer"
                    style={sty.daysInputContainer}
                    visible={currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.NUMBER_OF_DAYS}
                >
                    <InputSymphony
                        className="daysInput"
                        type="text"
                        label="Number of Days"
                        value={numberOfDays?.toString() || ''}
                        onChange={(value) => {
                            const days = Number(value);
                            // Ensure at least 1 day is selected
                            if (isNaN(days) || days < 1) {
                                setNumberOfDays(1);
                            }
                            // Don't allow more days than the budget supports at $10/day
                            else if (maxAllowedDays && days > maxAllowedDays) {
                                setNumberOfDays(maxAllowedDays);
                            } else {
                                setNumberOfDays(days);
                            }
                        }}
                        placeholder="Enter number of days"
                        maxWidth={200}
                    />
                    <DatePickerSymphony
                        label="Start Date"
                        placeholderText="Set start date"
                        minDate={getMinStartDate()}
                        selected={startDate ? dayjs(startDate).toDate() : null}
                        onChange={handleNumberOfDaysStartDateChange}
                        dateFormat="MMMM dd, yyyy"
                        maxWidth={200}
                    />
                </TabSymphony>
                {currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.NUMBER_OF_DAYS &&
                    maxAllowedDays !== undefined &&
                    budget &&
                    budget !== '' &&
                    <div style={styx(Sty.text12grey, sty.helperTextContainer)}>
                        Maximum {maxAllowedDays} days with current budget (${MINIMUM_DAILY_BUDGET}/day minimum)
                    </div>
                }
            </>
        )
    }

    const renderMaximumBudgetContent = () => {
        // Calculate minimum budget amount required for the start date (1 day at $10/day)
        const minRequiredBudget = MINIMUM_DAILY_BUDGET;

        return (
            <>
                <TabSymphony
                    className="maximumBudgetContent"
                    style={sty.daysInputContainer}
                    visible={currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.MAXIMUM_BUDGET}
                >
                    <div style={sty.dateInputWrapper}>
                        <InputSymphony
                            className="maximumBudgetInput"
                            type="text"
                            label="Maximum Budget"
                            format="money"
                            value={budgetMaximum?.toString() || ''}
                            onChange={(value) => {
                                if (value === '') {
                                    setBudgetMaximum('');
                                    return;
                                }

                                // Ensure the maximum budget is at least the minimum daily budget
                                const numValue = Number(value);
                                if (!isNaN(numValue) && numValue > 0 && numValue < minRequiredBudget) {
                                    setBudgetMaximum(minRequiredBudget.toString());
                                } else {
                                    setBudgetMaximum(value);
                                }
                            }}
                            placeholder="Enter maximum budget"
                            maxWidth={200}
                        />
                        <DatePickerSymphony
                            label="Start Date"
                            placeholderText="Set start date"
                            minDate={getMinStartDate()}
                            selected={startDate ? dayjs(startDate).toDate() : null}
                            onChange={handleNumberOfDaysStartDateChange}
                            dateFormat="MMMM dd, yyyy"
                        />
                    </div>
                </TabSymphony>
                {currentDateSelectionTab === CURRENT_DATE_SELECTION_TABS.MAXIMUM_BUDGET && (
                    <div style={styx(Sty.text12grey, sty.helperTextContainer)}>
                        Minimum budget ${minRequiredBudget.toFixed(2)} (${MINIMUM_DAILY_BUDGET}/day minimum)
                    </div>
                )}
            </>
        )
    }

    const getDateOptions = (): SelectOptionItem[] => {
        let dateOptions = DAILY_BUDGET_DATE_CAMPAIGN_OPTIONS;
        if (currentBudgetTab === CURRENT_BUDGET_TABS.LIFETIME) {
            dateOptions = LIFETIME_BUDGET_DATE_CAMPAIGN_OPTIONS;
        }

        if (adPlatform === AdPlatform.TIKTOK) {
            dateOptions = dateOptions.filter((option) => option.value !== CURRENT_DATE_SELECTION_TABS.NUMBER_OF_DAYS);
        }

        return dateOptions;
    }

    // NOTE: render()
    return (
        <CardSymphony
            className={clsx('datesCampaignComponent', className)}
            style={styx(sty.datesCampaignComponent, style)}
            title={title}
            subtitle={description}
            size="small"
            isPadding16
        >
            {/* <DebugBox
                alwaysShow  
                data={{
                    ...useCampaignBuilder()
                }}
            /> */}
            <InputSymphony
                className="dateCampaignInput"
                isSelectDropdown
                options={getDateOptions()}
                value={currentDateSelectionTab as string}
                onOptionSelect={(option) => {
                    setCurrentDateSelectionTab(option.value as CurrentDateSelectionTabType)
                    if (startDate && option.value === CURRENT_DATE_SELECTION_TABS.NUMBER_OF_DAYS) {
                        handleNumberOfDaysStartDateChange(new Date(startDate));
                    }
                }}
                label="This campaign should run:"
            />

            {renderDateRangeContent()}
            {renderNumberOfDaysContent()}
            {renderMaximumBudgetContent()}
        </CardSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    dateCampaignTabs: {
        width: '100%',
        overflowX: 'scroll',
    },
    dateRangeContent: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 16,
        width: '100%',
        alignItems: 'center',
    },
    dateRangeContent_mobile: {
        flexDirection: 'column',
    },
    dateInputWrapper: {
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
    },
    dateInputWrapper_maxDays: {
        flexDirection: 'row',
    },
    dateLabel: {
        marginRight: 4,
        whiteSpace: 'nowrap'
    },
    daysInputContainer: {
        display: 'flex',
        gap: 16,
        width: '100%'
    },
    calendar: {
        border: `1px solid ${Colors.grey}`,
        padding: 8,
        borderRadius: 8
    },
    dateDivider: {
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: 4,
        marginTop: 20
    },
    datePickerLabel: {
        fontSize: 14,
        color: Colors.greyDark,
        marginLeft: 4,
        marginBottom: 4
    },
    dateDividerLine: {
        height: 1,
        width: '100%',
        backgroundColor: Colors.greyLight
    },
    dateDividerText: {
        color: Colors.grey,
    },
    datesCampaignComponent: {
        gap: 16,
    },
    helperTextContainer: {
        marginTop: 8,
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end'
    }
}

export default DatesCampaign;